import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { TextField } from "@/components/form/TextField";
import { useAutosaveForm } from "@/components/form/useAutosaveForm";
import { SaveStatus } from "@/components/SaveStatus";

import { actionPlanKeys } from "../api/keys";
import { patchPlan } from "../api/hooks";
import type { ActionPlanDetail } from "../api/types";
import { NumberField } from "./fields/NumberField";

interface PlanSettingsDraft {
  name: string;
  time_planned_startyear: number | null;
  time_planned_endyear: number | null;
}

/**
 * Name + target-period editor for a plan. On-change autosave (the default for
 * every form) with a visible <SaveStatus>; a successful save invalidates the
 * list so the plan card's title/period stay in sync, and writes through to the
 * cached plan so the workspace header renames as you type.
 */
export function PlanSettingsForm({ plan }: { plan: ActionPlanDetail }) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { form, autosave } = useAutosaveForm<PlanSettingsDraft>({
    defaultValues: {
      name: plan.name ?? "",
      time_planned_startyear: plan.time_planned_startyear ?? null,
      time_planned_endyear: plan.time_planned_endyear ?? null,
    },
    autosave: {
      save: (data, signal) => patchPlan(plan.id, data, signal),
      onSaved: (data) => {
        void qc.invalidateQueries({ queryKey: actionPlanKeys.lists() });
        qc.setQueryData<ActionPlanDetail>(
          actionPlanKeys.detail(plan.id),
          (cached) => (cached ? { ...cached, ...data } : cached),
        );
      },
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      className="space-y-4"
    >
      <div className="flex items-center justify-end">
        <SaveStatus status={autosave.status} />
      </div>
      <form.Field name="name">
        {(field) => (
          <TextField
            field={field}
            label={t("actionPlans.list.nameLabel")}
            placeholder={t("actionPlans.list.namePlaceholder")}
          />
        )}
      </form.Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <form.Field name="time_planned_startyear">
          {(field) => (
            <NumberField field={field} label={t("actionPlans.list.startYear")} />
          )}
        </form.Field>
        <form.Field name="time_planned_endyear">
          {(field) => (
            <NumberField field={field} label={t("actionPlans.list.endYear")} />
          )}
        </form.Field>
      </div>
    </form>
  );
}
